/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { useIntl } from 'react-intl'

import type { EuiDescriptionListProps } from '@elastic/eui'
import { EuiDescriptionList, EuiHorizontalRule, EuiPanel, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { LineItem } from '@modules/billing-api/customTypes'
import { useConfig } from '@modules/cui/ConfigContext'

import InternalField from './InternalField'

const MarketplaceSection = ({ lineItem }: { lineItem: LineItem }) => {
  const { formatMessage } = useIntl()
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  if (!lineItem.external_payment_account) {
    return null
  }

  const getListItems = () => {
    if (lineItem.external_payment_account?.gcp) {
      return [
        {
          title: formatMessage({
            id: 'line-item.details-flyout.mp.gcp.entitlement_id',
            defaultMessage: 'Entitlement ID',
          }),
          isVisible: true,
          description: lineItem.external_payment_account.gcp.entitlement_id,
        },
        {
          title: formatMessage({
            id: 'line-item.details-flyout.mp.gcp.quote_id',
            defaultMessage: 'Quote ID',
          }),
          isVisible: true,
          description: lineItem.external_payment_account.gcp.quote_id,
        },
        {
          title: (
            <InternalField>
              <FormattedMessage id='line-item.details-flyout.mp.gcp.plan' defaultMessage='Plan' />
            </InternalField>
          ),
          isVisible: isAdminConsole,
          description: lineItem.external_payment_account.gcp.plan,
        },
        {
          title: (
            <InternalField>
              <FormattedMessage id='line-item.details-flyout.mp.gcp.state' defaultMessage='State' />
            </InternalField>
          ),
          isVisible: isAdminConsole,
          description: lineItem.external_payment_account.gcp.state,
        },
      ].filter((item) => item.isVisible)
    }

    if (lineItem.external_payment_account?.aws) {
      return [
        {
          title: formatMessage({
            id: 'line-item.details-flyout.mp.aws.account_id',
            defaultMessage: 'Account ID',
          }),
          isVisible: true,
          description: lineItem.external_payment_account.aws.account_id,
        },
        {
          title: (
            <InternalField>
              <FormattedMessage
                id='line-item.details-flyout.mp.aws.customer_id'
                defaultMessage='Customer ID'
              />
            </InternalField>
          ),
          isVisible: isAdminConsole,
          description: lineItem.external_payment_account.aws.customer_id,
        },
        {
          title: (
            <InternalField>
              <FormattedMessage
                id='line-item.details-flyout.mp.aws.product_code'
                defaultMessage='Product code'
              />
            </InternalField>
          ),
          isVisible: isAdminConsole,
          description: lineItem.external_payment_account.aws.product_code,
        },
        {
          title: (
            <InternalField>
              <FormattedMessage id='line-item.details-flyout.mp.aws.sku' defaultMessage='SKU' />
            </InternalField>
          ),
          isVisible: isAdminConsole,
          description: lineItem.external_payment_account.aws.sku,
        },
      ].filter((item) => item.isVisible)
    }

    return []
  }

  return (
    <Fragment>
      <EuiHorizontalRule margin='xs' />
      <EuiPanel paddingSize='m' hasBorder={false} hasShadow={false}>
        <EuiText size='s'>
          <h4>
            <FormattedMessage
              id='line-item.details-flyout.mp.title'
              defaultMessage='Marketplace account'
            />
          </h4>
        </EuiText>
        <EuiSpacer />
        <EuiDescriptionList
          type='column'
          compressed={true}
          columnGutterSize='m'
          listItems={getListItems() as EuiDescriptionListProps['listItems']}
        />
      </EuiPanel>
    </Fragment>
  )
}

export default MarketplaceSection
